import type { Tool, Trace } from '@/features/traceability/type/Tool';
import { sanitizeReference } from './reference';

export type ToolDashboardEntry = {
  toolId: string;
  totalPieces: number;
  totalMinutes: number;
  totalVolume: number;
  avgMalround: number | null;
  /** Raw normalised state string from backend (lowercase, trimmed). Never null. */
  finalState: string;
  machine: string | null;
  operator: string | null;
  material: string | null;
  avgVc: number | null;
  avgFz: number | null;
  avgAp: number | null;
  avgAe: number | null;
};

export type ReferenceStats = {
  totalTools: number;
  avgPieces: number;
  bestLife: number;
  worstLife: number;
  totalPieces: number;
  totalMinutes: number;
  avgMinutesPerTool: number;
  avgMinutesPerPiece: number | null;
  totalVolume: number;
  avgMalround: number | null;
  /** Count per real backend state value (e.g. { new: 3, active: 5, worn: 2, broken: 1 }) */
  stateCounts: Record<string, number>;
};

const ANOMALY_THRESHOLD = 0.65;
export { ANOMALY_THRESHOLD };

/** Normalise a raw backend state string. Returns 'unknown' when absent. */
function normalizeFinalState(stateValue: string | null | undefined): string {
  const s = stateValue?.toLowerCase().trim();
  return s || 'unknown';
}

function avgNonNull(values: (number | null)[]): number | null {
  const valid = values.filter((v): v is number => v !== null);
  if (!valid.length) return null;
  return valid.reduce((a, b) => a + b, 0) / valid.length;
}

function entryFromTool(tool: Tool): ToolDashboardEntry {
  const traces: Trace[] = Array.isArray(tool.traces) ? tool.traces : [];
  const lastTrace = traces.length > 0 ? traces[traces.length - 1] : null;

  const totalPieces = traces.reduce((sum, t) => sum + (t.machinedPieces ?? 0), 0);
  // NB: accumulates the stored durations as-is, i.e. SECONDS (despite the field name);
  // KpiStrip divides by 60 and labels the result in minutes.
  const totalMinutes = traces.reduce((sum, t) => sum + (t.totalMachiningDuration ?? 0), 0);
  const totalVolume = traces.reduce((sum, t) => sum + (t.machinedVolume ?? 0), 0);
  const finalState = normalizeFinalState(lastTrace?.toolState?.toolState);

  return {
    toolId: tool.toolId,
    totalPieces,
    totalMinutes,
    totalVolume,
    avgMalround: avgNonNull(traces.map((t) => t.malround)),
    finalState,
    machine: lastTrace?.machine?.model ?? null,
    operator: lastTrace?.operator ?? null,
    material: lastTrace?.material?.name ?? null,
    avgVc: avgNonNull(traces.map((t) => t.vc)),
    avgFz: avgNonNull(traces.map((t) => t.fz)),
    avgAp: avgNonNull(traces.map((t) => t.ap)),
    avgAe: avgNonNull(traces.map((t) => t.ae)),
  };
}

export function computeToolDashboardEntries(tools: Tool[], reference: string): ToolDashboardEntry[] {
  return tools
    .filter((tool) => sanitizeReference(tool.reference) === sanitizeReference(reference))
    .map(entryFromTool);
}

export function computeReferenceStats(entries: ToolDashboardEntry[]): ReferenceStats {
  const empty: ReferenceStats = {
    totalTools: 0, avgPieces: 0, bestLife: 0, worstLife: 0,
    totalPieces: 0, totalMinutes: 0, avgMinutesPerTool: 0,
    avgMinutesPerPiece: null, totalVolume: 0, avgMalround: null,
    stateCounts: {},
  };
  if (!entries.length) return empty;

  const withPieces = entries.filter((e) => e.totalPieces > 0);
  const totalPieces = entries.reduce((sum, e) => sum + e.totalPieces, 0);
  const totalMinutes = entries.reduce((sum, e) => sum + e.totalMinutes, 0);
  const totalVolume = entries.reduce((sum, e) => sum + e.totalVolume, 0);
  const avgPieces = withPieces.length > 0 ? totalPieces / withPieces.length : 0;
  const bestLife = withPieces.length > 0 ? Math.max(...withPieces.map((e) => e.totalPieces)) : 0;
  const worstLife = withPieces.length > 0 ? Math.min(...withPieces.map((e) => e.totalPieces)) : 0;
  const avgMinutesPerTool = entries.length > 0 ? totalMinutes / entries.length : 0;
  const avgMinutesPerPiece = totalPieces > 0 ? totalMinutes / totalPieces : null;
  const avgMalround = avgNonNull(entries.map((e) => e.avgMalround));

  return {
    totalTools: entries.length,
    avgPieces,
    bestLife,
    worstLife,
    totalPieces,
    totalMinutes,
    avgMinutesPerTool,
    avgMinutesPerPiece,
    totalVolume,
    avgMalround,
    stateCounts: entries.reduce<Record<string, number>>((acc, e) => {
      acc[e.finalState] = (acc[e.finalState] ?? 0) + 1;
      return acc;
    }, {}),
  };
}
